import dataStorageManager, { BucketDefaultIDs, DMS_Operation } from '../../managers/data/DataStorageManager'
import dataQueryManager from '../../managers/network/DataQueryManager'
import { IEmbarcacion } from './EmbarcacionesServices'
import { IArmador } from './ArmadoresServices'
import { IRequest } from './RequestsServices'

export enum EstadoAO {
  VIGENTE = 0,
  SUSPENDIDA = 1,
  CADUCADA = 2,
  CANCELADA = 3,
  REEMPLAZADA = 4,
}

export enum TipoSuspensionAO {
  NINGUNA = 0,
  CN_VENCIDO = 1,
  ADMINISTRATIVA = 2,
}

export enum TipoAutorizacionAO {
  OPERACION_COMERCIAL = 0,
  SERVICIO = 1,
}

export interface IAutorizacionOperacion {
  id: number
  numero_ao: string
  solicitud_id: number | null
  embarcacion_id: number
  armador_id: number
  tipo_autorizacion: TipoAutorizacionAO
  estado: EstadoAO
  tipo_suspension: TipoSuspensionAO
  motivo_suspension: string | null
  motivo_cancelacion: string | null
  motivo_modificacion_vigencia: string | null
  fecha_inicio: string
  fecha_vencimiento: string
  fecha_vencimiento_cn: string | null
  posee_convenio: boolean
  pdf_url: string | null
  usuario_creacion: number | null
  usuario_resolucion: number | null
  created_at?: string
  updated_at?: string
}

export interface OperatedAutorizacionOperacion extends IAutorizacionOperacion {
  embarcacionData?: IEmbarcacion
  armadorData?: IArmador
  solicitudData?: IRequest
}

export const VIGENCIA_ESTANDAR_AO_ANOS = 3

export const defaultIAutorizacionOperacion: IAutorizacionOperacion = {
  id: 0,
  numero_ao: '',
  solicitud_id: null,
  embarcacion_id: 0,
  armador_id: 0,
  tipo_autorizacion: TipoAutorizacionAO.OPERACION_COMERCIAL,
  estado: EstadoAO.VIGENTE,
  tipo_suspension: TipoSuspensionAO.NINGUNA,
  motivo_suspension: null,
  motivo_cancelacion: null,
  motivo_modificacion_vigencia: null,
  fecha_inicio: new Date().toISOString(),
  fecha_vencimiento: new Date(Date.now() + VIGENCIA_ESTANDAR_AO_ANOS * 365 * 24 * 60 * 60 * 1000).toISOString(),
  fecha_vencimiento_cn: null,
  posee_convenio: false,
  pdf_url: null,
  usuario_creacion: null,
  usuario_resolucion: null,
}

export default class AutorizacionOperacionServices {
  private bucketID: string | null = null

  constructor() {
    this.bucketID = dataStorageManager.registerBucket<IAutorizacionOperacion>(this.getAllAOs.bind(this), {
      refreshRatio: 5 * 60 * 1000,
      bucketID: BucketDefaultIDs.OTROS,
      operations: new Map<string, DMS_Operation>([
        [
          'insertAO',
          {
            operation: this.insertAO.bind(this),
            reloadAfter: true,
          },
        ],
        [
          'updateAO',
          {
            operation: this.updateAO.bind(this),
            reloadAfter: true,
          },
        ],
      ]),
    })
  }

  public async getAllAOs(): Promise<IAutorizacionOperacion[]> {
    try {
      const response = await dataQueryManager.addQuery<IAutorizacionOperacion[]>({
        query: `${import.meta.env.VITE_BASE_URL}/autorizacion_operacion/1/op_select_ao`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
      })
      if (response.data && Array.isArray(response.data)) {
        return response.data
      }
      return []
    } catch (e) {
      console.warn('Error al obtener Autorizaciones de Operación:', e)
      return []
    }
  }

  public async insertAO(ao: Partial<IAutorizacionOperacion>): Promise<boolean> {
    try {
      const response = await dataQueryManager.addQuery({
        query: `${import.meta.env.VITE_BASE_URL}/autorizacion_operacion/1/op_insert_ao`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body: { op_insert_ao: ao },
      })
      return !!response
    } catch (e) {
      console.error('Error al insertar AO:', e)
      return false
    }
  }

  public async updateAO(ao: Partial<IAutorizacionOperacion>): Promise<boolean> {
    try {
      const response = await dataQueryManager.addQuery({
        query: `${import.meta.env.VITE_BASE_URL}/autorizacion_operacion/1/op_update_ao`,
        operation: 'POST',
        headers: { 'Content-Type': 'application/json' },
        params: [],
        body: { op_update_ao: ao },
      })
      return !!response
    } catch (e) {
      console.error('Error al actualizar AO:', e)
      return false
    }
  }

  /**
   * Helper para verificar si una embarcación tiene una AO activa (Vigente)
   */
  public static tieneAOActiva(embarcacionId: number, aoList: IAutorizacionOperacion[]): boolean {
    return aoList.some((ao) => ao.embarcacion_id === embarcacionId && ao.estado === EstadoAO.VIGENTE)
  }

  /**
   * Helper para verificar si una embarcación tiene alguna AO en estado Suspendida
   */
  public static tieneAOSuspendida(embarcacionId: number, aoList: IAutorizacionOperacion[]): boolean {
    return aoList.some((ao) => ao.embarcacion_id === embarcacionId && ao.estado === EstadoAO.SUSPENDIDA)
  }
}
